import { mapGetters } from 'vuex'
import _ from 'lodash'
import * as actionTypes from '@/store/action-types'
import roleTypes from '@/store/roles'
import paddleSubscriptionStatusTypes from '@/store/paddle-subscription-status-types'
import { planPublicIds } from '@/shared/constants/pricing/planIds'
import operators from '@/shared/constants/comparisonOperators'
import { featureSlugs } from '@/shared/constants/packagebuilder/features'
import timePeriods from '@/store/time-periods'
import moment from 'moment'

export default {
  data: function () {
    return {
      value: null,
      landingPageLimitations: {
        canCreate: true,
        canGenerate: true,
        canProceed: true,
        message: null,
        currStorageCount: 0,
        currMonthlyGenerations: 0,
        noOfLandingPages: 0,
        noOfAiGeneratedLandingPages: 0
      }
    }
  },

  methods: {
    // Check if user has active subscription
    hasActiveSubscriptionByUser: function (user) {
      let subscription = this.getSubscriptionByUser(user)

      if (!subscription) {
        return false
      }

      return subscription.active
    },

    // Get user's current plan
    getPlanByUser: function (user) {
      if (!user.plan) {
        return null
      }

      return user.plan
    },

    // Get user's subscription details
    getSubscriptionByUser: function (user) {
      let plan = this.getPlanByUser(user)

      if (!plan) {
        return null
      }

      return plan.subscription
    },

    // Check feature access permissions
    userMixin_hasFeatureAccess: function (featureSlug) {
      try {
        // During logout/navigation teardown, user can be cleared before UI unmounts.
        // Treat this as a normal "no access" state without noisy warnings.
        if (!this.user) {
          return false
        }

        if (this.isAdmin || this.isSuperCustomer) {
          return true
        }

        if (!this.plan) {
          return false
        }

        // Check if plan_features exists and is an array
        if (!this.plan?.plan_features || !Array.isArray(this.plan?.plan_features)) {
          return false
        }

        // Check if feature exists in plan
        const hasFeature = this.plan?.plan_features?.some(element => element && element.slug === featureSlug)
        return hasFeature
      } catch (error) {
        // Graceful degradation: assume feature not available on error
        console.error(`Error checking feature access for "${featureSlug}":`, error)
        return false
      }
    },

    // Check feature property access with comparison
    userMixin_hasFeaturePropertyAccess: function (featureSlug, operator, inputArray) {
      if (this.isAdmin || this.isSuperCustomer) {
        return true
      }
      return this.$store.dispatch(actionTypes.FETCH_USER, {
        userId: this.user.id
      })
        .then((response) => {
          let data = response.data.data
          this.value = this.checkFeaturePropertyAccess(data.plan, featureSlug, operator, inputArray)
          return this.value
        })
    },

    // Validate feature property access rules
    checkFeaturePropertyAccess: function (plan, featureSlug, operator, inputArray) {
      try {
        // Check if plan exists and has plan_features
        if (!plan || !plan?.plan_features || !Array.isArray(plan?.plan_features)) {
          return false
        }

        let planFeatures = plan?.plan_features?.find(elm => elm && elm.slug === featureSlug)
        if (!planFeatures) {
          return false
        }

        // Check if feature has properties
        if (!planFeatures?.feature_properties || !Array.isArray(planFeatures?.feature_properties)) {
          return false
        }

        let planFeatureProperties = planFeatures?.feature_properties?.filter(element => element && element.feature_id === planFeatures.id)
        if (!planFeatureProperties || planFeatureProperties.length === 0) {
          return false
        }

        let result = []
        inputArray.forEach(input => {
          planFeatureProperties.forEach(pp => {
            if (input['limitation_title'] === pp.title) {
              let limitationValue = input['limitation_value']
              if (
                pp.reset_period === timePeriods.MONTHLY &&
                input['limitation_valueMonthly'] !== undefined &&
                input['limitation_valueMonthly'] !== null
              ) {
                limitationValue = input['limitation_valueMonthly']
              } else if (
                pp.reset_period === timePeriods.YEARLY &&
                input['limitation_valueYearly'] !== undefined &&
                input['limitation_valueYearly'] !== null
              ) {
                limitationValue = input['limitation_valueYearly']
              } else if (
                pp.reset_period === timePeriods.AS_PER_PLAN &&
                input['limitation_valueAsPerPlan'] !== undefined &&
                input['limitation_valueAsPerPlan'] !== null
              ) {
                limitationValue = input['limitation_valueAsPerPlan']
              }

              result.push(
                this.userMixin_dynComparison(
                  limitationValue,
                  pp.value,
                  input['compare']
                )
              )
            }
          })
        })

        return operator === 'OR' ? result.some(res => res === true) : !result.some(res => res === false)
      } catch (error) {
        // Graceful degradation: assume feature not available on error
        console.error(`Error checking feature property access for "${featureSlug}":`, error)
        return false
      }
    },

    // Dynamic comparison helper
    userMixin_dynComparison: function (inputValue, comparisonValue, operator) {
      const comparisonOperators = {
        [operators.EQ]: inputValue === comparisonValue,
        [operators.LT]: inputValue < comparisonValue,
        [operators.LTE]: inputValue <= comparisonValue,
        [operators.GT]: inputValue > comparisonValue,
        [operators.GTE]: inputValue >= comparisonValue
      }
      return comparisonOperators[operator] || false
    },

    // Check landing page limitations
    async userMixin_checkLpLimitations(getLimitationsFunc) {
      try {
        // Avoid unnecessary API calls for users without landing page access.
        if (!this.userMixin_hasFeatureAccess(featureSlugs.LANDING_PAGE)) {
          return this.landingPageLimitations
        }

        // Use the provided API function to avoid circular dependency
        if (!getLimitationsFunc) {
          return this.landingPageLimitations
        }

        const response = await getLimitationsFunc()
        // Handle Laravel apiResponse format
        const responseData = response?.data?.data || response?.data || response

        if (responseData) {
          this.landingPageLimitations = {
            canCreate: responseData.canCreate ?? true,
            canGenerate: responseData.canGenerate ?? true,
            canProceed: responseData.canProceed ?? true,
            message: responseData.message || null,
            currStorageCount: responseData.currStorageCount || 0,
            currMonthlyGenerations: responseData.currMonthlyGenerations || 0,
            noOfLandingPages: parseInt(responseData.noOfLandingPages) || 0,
            noOfAiGeneratedLandingPages: parseInt(responseData.noOfAiGeneratedLandingPages) || 0
          }
        }

        return this.landingPageLimitations
      } catch (error) {
        // On error, allow creation (fail-safe) without noisy logs.
        this.landingPageLimitations = {
          canCreate: true,
          canGenerate: true,
          canProceed: true,
          message: null,
          currStorageCount: 0,
          currMonthlyGenerations: 0,
          noOfLandingPages: 0,
          noOfAiGeneratedLandingPages: 0
        }
        return this.landingPageLimitations
      }
    }
  },

  computed: {
    ...mapGetters(['user']),

    // Basic user plan and subscription getters
    plan: function () {
      return this.user?.plan
    },

    subscription: function () {
      if (!this.user) {
        return null
      }

      return this.user?.plan?.subscription
    },

    // Subscription status checks
    hasSubscription: function () {
      if (this.subscription) {
        return true
      }

      return false
    },

    hasCancelledSubscription: function () {
      if (!this.subscription && !this.plan) {
        return false
      }
      const sub = this.subscription || {}
      const plan = this.plan || {}
      // Check if cancelled: has ends_at OR status is deleted
      // For cancel, ends_at is the primary indicator
      return (
        sub.ends_at ||
        plan.ends_at ||
        sub.status === paddleSubscriptionStatusTypes.DELETED ||
        plan.status === paddleSubscriptionStatusTypes.DELETED
      )
    },

    // User role checks
    isAdmin: function () {
      if (!this.user) {
        return false
      }

      return !_.isNil(_.find(this.user.roles, { name: roleTypes.ADMIN }))
    },

    isSuperCustomer: function () {
      if (!this.user) {
        return false
      }

      return !_.isNil(_.find(this.user.roles, { name: roleTypes.SUPER_CUSTOMER }))
    },

    isCustomer: function () {
      if (!this.user) {
        return false
      }

      return !_.isNil(_.find(this.user.roles, { name: roleTypes.CUSTOMER }))
    },

    // User verification status
    isVerified: function () {
      return this.user && !this.user.verified
    },

    // User data synchronization
    syncUser: function () {
      if (!this.user) {
        return
      }

      this.$store
        .dispatch(actionTypes.FETCH_USER, {
          userId: this.user.id
        })
        .then(response => {
          let user = response.data.data

          this.$store.dispatch(actionTypes.UPDATE_USER_LOCAL, user).then(() => {
            localStorage.setItem('user', JSON.stringify(user))
          })
        })
    },

    // Trial and plan status checks
    userMixin_hasFreeTrialDefaultPlan: function () {
      if (!this.user) {
        return false
      }
      return this.user.defaultPlan.public_id === planPublicIds.freeTrial
    },

    // Check if user has free trial plan - checks plan public_id
    userMixin_hasActiveFreeTrialPlan: function () {
      if (!this.plan) {
        return false
      }
      return this.plan.public_id === planPublicIds.freeTrial
    },

    // Get remaining trial days - returns trial_days_left from plan
    userMixin_trialDaysLeft: function () {
      if (!this.plan) {
        return null
      }
      return this.plan.trial_days_left
    },

    // Check if user is currently in trial - checks in_trial flag
    userMixin_inTrial: function () {
      if (!this.plan) {
        return false
      }
      return this.plan.in_trial
    },

    // Check if plan/account has expired - checks plan_expired flag
    userMixin_planExpired: function () {
      if (!this.plan) {
        return false
      }
      return this.plan.plan_expired
    },

    // Check if trial has ended - user has free_trial plan but in_trial is false
    userMixin_trialEnded: function () {
      if (!this.plan) {
        return false
      }
      return this.plan.public_id === planPublicIds.freeTrial && !this.plan.in_trial
    },

    // Check if account is expired - plan_expired is true
    userMixin_accountExpired: function () {
      if (!this.plan) {
        return false
      }
      return this.plan.plan_expired === true
    },

    // Plan type checks
    userMixin_hasActiveFreePlan: function () {
      // Check if user has free plan (not free_trial)
      if (!this.plan) {
        return false
      }
      return this.plan.public_id === planPublicIds.free
    },

    // Payment status checks
    userMixin_isPastDueUser: function () {
      if (!this.subscription) {
        return false
      }
      return this.subscription.status === paddleSubscriptionStatusTypes.PAST_DUE
    },

    // Subscription pause status
    // According to Paddle docs: when paused_from exists, subscription is paused or scheduled to pause
    // Status may still be 'active' until paused_from date, but user can only resume/cancel
    userMixin_hasPausedSubscription: function () {
      if (!this.subscription && !this.plan) {
        return false
      }
      const sub = this.subscription || {}
      const plan = this.plan || {}
      const pausedFrom = sub?.paused_from || plan?.paused_from
      // Check if paused: status is paused OR has paused_at OR has paused_from (scheduled to pause)
      return Boolean(
        sub?.status === paddleSubscriptionStatusTypes.PAUSED ||
        sub?.paused_at ||
        pausedFrom
      )
    },

    userMixin_hasPausedSubscriptionInGrace: function () {
      if (!this.userMixin_hasPausedSubscription) {
        return false
      }

      const sub = this.subscription || {}
      const plan = this.plan || {}
      const pausedFrom = sub.paused_from || plan.paused_from

      if (!pausedFrom) {
        return false
      }

      let presentDate = moment.utc(new Date()).format('YYYY-MM-DD')
      let pausedDate = moment.utc(pausedFrom).format('YYYY-MM-DD')

      return pausedDate > presentDate
    }
  }
}
